import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useScenarioContext } from '@/shared/providers/ScenarioProvider';
import Permission from '@/shared/utils/permissions/Permission';
import useDesignStore from '@/components/DesignContainer/hooks/useDesignStore';
import getActionName from './utils/getActionName';

export const DispatchContext = createContext();
export const useDispatchContext = () => useContext(DispatchContext);

const DispatchProvider = ({ children, action, isEditable, name, uuid }) => {
  const actions = useDesignStore((state) => state.actions);
  const { access } = useScenarioContext();
  const [canEdit, setCanEdit] = useState();
  const { showBoundary } = useErrorBoundary();

  // Determines if the user can perform a dispatch based on their permissions.
  const handleCanEdit = (propertyName) => {
    if (!access?.can || !isEditable || !uuid) {
      return;
    }

    const permission = Permission[action];

    if (!permission?.__type) {
      return;
    }

    const entity = { uuid, __type: permission.__type };

    const property =
      permission?.property ?? permission?.properties.get(propertyName ?? name);

    if (!property) {
      return;
    }

    const hasAccess = access.can(entity, property, permission.action);

    setCanEdit(access.can(entity, property, permission.action));
  };

  // Sets the state for users being able to dispatch an event.
  useEffect(() => {
    if (access?.isReady) {
      handleCanEdit(name);
    }
  }, [access?.isReady, action, isEditable, name]);

  // Performs a dispatch
  const handleDispatch = async ({ action: dispatchAction, properties }) => {
    try {
      await actions.dispatch(dispatchAction ?? action, properties);
    } catch (error) {
      showBoundary({ ...error });
    }
  };

  // Performs a dispatch based on the entity provided.
  const handleUpdate = async ({ action, entityType, properties }) => {
    const actionName = getActionName(entityType, action);
    try {
      await actions.dispatch(actionName, properties);
    } catch (error) {
      showBoundary({ ...error });
    }
  };

  const context = useMemo(() => {
    return {
      canEdit,
      handleDispatch,
      onUpdate: handleUpdate,
    };
  }, [actions, canEdit]);

  return (
    <DispatchContext.Provider value={context}>
      {children}
    </DispatchContext.Provider>
  );
};

export default DispatchProvider;
